import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Pill, Calendar, Pencil, ShieldCheck, ShieldAlert } from "lucide-react";
import RouteIcon from "@/components/RouteIcon";
import { useAuth } from "@/lib/auth";

export default function RegimenDetailPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const isSuper = user?.role === "admin" || user?.role === "organizer";
  const canEdit = r && (isSuper || r.owner_id === user?.id);

  useEffect(() => { api.get(`/regimens/${id}`).then((res) => setR(res.data)); }, [id]);

  if (!r) return <div className="p-8 text-stone-500">Loading regimen…</div>;

  const statusBadge = () => {
    const s = r.approval_status || "approved";
    if (s === "approved") return <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Approved</span>;
    if (s === "pending")  return <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" />Pending admin approval</span>;
    if (s === "rejected") return <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" />Rejected</span>;
    return null;
  };

  return (
    <div className="p-8 max-w-[1200px]" data-testid="regimen-detail-page">
      <Link to="/regimens" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-4" data-testid="back-to-regimens">
        <ArrowLeft className="w-4 h-4" />Back to regimens
      </Link>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">{r.name}</h1>
          <div className="text-stone-500 mt-1 flex flex-wrap items-center gap-2">
            <span className="px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">{r.visibility}</span>
            {statusBadge()}
            <span>{r.disease_type}</span>
            <span>•</span>
            <span>{r.cycles.length} cycle{r.cycles.length !== 1 ? "s" : ""}</span>
            <span>•</span>
            <span>by {r.owner_name}</span>
          </div>
          {r.description && <p className="mt-3 text-stone-600 max-w-3xl">{r.description}</p>}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {canEdit && (
            <button onClick={() => nav(`/regimens/${r.id}/edit`)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-300 hover:bg-stone-50 font-semibold" data-testid="edit-regimen-btn">
              <Pencil className="w-4 h-4" />Edit
            </button>
          )}
          <button onClick={() => nav(`/calendars/new?regimen_id=${r.id}`)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold" data-testid="generate-calendar-btn">
            <Calendar className="w-4 h-4" />Generate calendar
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {r.cycles.map((c, idx) => (
          <div key={idx} className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden" data-testid={`cycle-${idx}`}>
            <div className="px-5 py-3 bg-brand-50 border-b border-stone-100 flex items-center justify-between">
              <div>
                <h3 className="font-display font-semibold text-brand-700">{c.label}</h3>
                <div className="text-xs text-brand-600 mt-0.5">Length: {c.length_days} days</div>
              </div>
              <Pill className="w-5 h-5 text-brand-500" />
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium">Drug</th>
                  <th className="text-left px-5 py-2.5 font-medium">Dose</th>
                  <th className="text-left px-5 py-2.5 font-medium">Days</th>
                  <th className="text-left px-5 py-2.5 font-medium">Route</th>
                  <th className="text-left px-5 py-2.5 font-medium">Location</th>
                  <th className="text-left px-5 py-2.5 font-medium whitespace-nowrap">Infusion</th>
                  <th className="text-left px-5 py-2.5 font-medium whitespace-nowrap">Obs.</th>
                  <th className="text-left px-5 py-2.5 font-medium">Dose cap</th>
                  <th className="text-left px-5 py-2.5 font-medium">Warnings</th>
                </tr>
              </thead>
              <tbody>
                {c.drugs.map((d, i) => (
                  <tr key={i} className="border-t border-stone-100 hover:bg-stone-50 align-top">
                    <td className="px-5 py-3 font-semibold text-stone-800">{d.drug_name}{d.brand ? <span className="text-stone-500 font-normal"> ({d.brand})</span> : null}</td>
                    <td className="px-5 py-3 text-stone-600">{d.dose}</td>
                    <td className="px-5 py-3 text-stone-600 font-mono text-xs">{d.days_pattern}</td>
                    <td className="px-5 py-3 text-stone-600">
                      <RouteIcon route={d.route} withLabel size="sm" />
                    </td>
                    <td className="px-5 py-3 text-stone-600">{d.location}</td>
                    <td className="px-5 py-3 text-stone-600">{d.infusion_duration_min ? `${d.infusion_duration_min} min` : <span className="text-stone-400">—</span>}</td>
                    <td className="px-5 py-3 text-stone-600">{d.observation_time_min ? `${d.observation_time_min} min` : <span className="text-stone-400">—</span>}</td>
                    <td className="px-5 py-3 text-stone-600">{d.dose_cap || <span className="text-stone-400">—</span>}</td>
                    <td className="px-5 py-3 text-stone-600 max-w-[200px]">{d.warnings ? <span className="text-amber-700">{d.warnings}</span> : <span className="text-stone-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
